import { Reducer } from "redux";
import {
  FAILURE,
  REPORTS_REQUEST,
  CF_REPORTS_SUCCESS,
  SLICES_REQUEST,
  SLICES_SUCCESS,
  MTM_REPORTS_SUCCESS,
  VAR_REPORTS_SUCCESS,
  ALERTS_REQUEST,
  ALERTS_SUCCESS,
  SUMMARY_REQUEST,
  SUMMARY_SUCCESS,
  INSTRUMENT_REQUEST,
  INSTRUMENT_SUCCESS,
  SET_SLICE,
  INSTRUMENT_SUMMARY_SUCCESS,
  SET_TKR_CUSIP,
  SET_SUMMARY_DATE,
  PROJECTED_VAR_REPORTS_SUCCESS,
  PROJECTED_VAR_REPORTS_FAILURE,
  CORRESPONDENT_SUCCESS,
  SLICES_FAILURE,
  ALERTS_FAILURE,
  SUMMARY_FAILURE,
  INSTRUMENT_FAILURE,
  INSTRUMENT_SUMMARY_FAILURE,
  CORRESPONDENT_FAILURE,
  CF_REPORTS_FAILURE,
  MTM_REPORTS_FAILURE,
  VAR_REPORTS_FAILURE,
} from "./constants";
import {
  AlertItem,
  CFItem,
  InstrumentItem,
  MtmItem,
  Slice,
  VarItem,
  TkrCusip,
  CorrespondentItem,
  DetailedError,
} from "../../types";

export interface ReportState {
  readonly isCFReportLoading: boolean;
  readonly isMTMReportLoading: boolean;
  readonly isVARReportLoading: boolean;
  readonly isProjectedVARReportLoading: boolean;
  readonly isSummaryLoading: boolean;
  readonly isSlicesLoading: boolean;
  readonly isInstrumentLoading: boolean;
  readonly isInstrumentSummaryLoading: boolean;
  readonly isCorrespondentLoading: boolean;
  readonly cfReport: CFItem;
  readonly mtmReport: MtmItem;
  readonly varReport: VarItem;
  readonly projectedVarReport: VarItem;

  readonly slices: string[];
  readonly error: boolean;
  readonly projectedVarReportError: DetailedError;

  readonly authError: boolean;
  readonly varReportError: DetailedError;
  readonly mtmReportError: DetailedError;
  readonly cfReportError: DetailedError;
  readonly slicesError: DetailedError;
  readonly alertsError: DetailedError;
  readonly summaryError: DetailedError;
  readonly instrumentError: DetailedError;
  readonly instrumentSummaryError: DetailedError;
  readonly correspondentError: DetailedError;

  readonly errorDetails: any;
  readonly alerts: AlertItem[];
  readonly summary: CFItem;
  readonly instrument: InstrumentItem;
  readonly instrumentSummary: InstrumentItem;
  readonly slice: Slice;
  readonly tkrCusip: TkrCusip;
  readonly summaryDate: string;
  readonly correspondent: CorrespondentItem;
}

export const initState: ReportState = {
  cfReport: <CFItem>{},
  mtmReport: <MtmItem>{},
  varReport: <VarItem>{},
  projectedVarReport: <VarItem>{},
  slices: [],
  authError: false,
  isCFReportLoading: false,
  isMTMReportLoading: false,
  isVARReportLoading: false,
  isProjectedVARReportLoading: false,
  isSummaryLoading: false,
  isSlicesLoading: false,
  isInstrumentLoading: false,
  isInstrumentSummaryLoading: false,
  isCorrespondentLoading: false,
  error: false,
  projectedVarReportError: <DetailedError>{},
  varReportError: <DetailedError>{},
  mtmReportError: <DetailedError>{},
  cfReportError: <DetailedError>{},
  slicesError: <DetailedError>{},
  alertsError: <DetailedError>{},
  summaryError: <DetailedError>{},
  instrumentError: <DetailedError>{},
  instrumentSummaryError: <DetailedError>{},
  correspondentError: <DetailedError>{},
  alerts: [],
  summary: <CFItem>{},
  instrument: <InstrumentItem>{},
  instrumentSummary: <InstrumentItem>{},
  slice: <Slice>{},
  tkrCusip: <TkrCusip>{},
  errorDetails: {},
  summaryDate: "",
  correspondent: <CorrespondentItem>{},
};

const reducer: Reducer<ReportState> = (state = initState, action) => {
  switch (action.type) {
    case REPORTS_REQUEST:
      return {
        ...state,
        isCFReportLoading: true,
        isMTMReportLoading: true,
        isVARReportLoading: true,
        isProjectedVARReportLoading: true,
      };
    case SLICES_REQUEST:
      return { ...state, isSlicesLoading: true };
    case ALERTS_REQUEST:
      return { ...state };
    case SUMMARY_REQUEST:
      return { ...state, isSummaryLoading: true };
    case INSTRUMENT_REQUEST:
      return {
        ...state,
        isInstrumentLoading: true,
        isInstrumentSummaryLoading: true,
        isCorrespondentLoading: true,
      };
    case CF_REPORTS_SUCCESS:
      return {
        ...state,
        cfReport: action.payload,
        isCFReportLoading: false,
      };
    case CF_REPORTS_FAILURE:
      return {
        ...state,
        cfReportError: {
          error: true,
          details: action.payload.response,
        },
        isCFReportLoading: false,
        authError: action.payload.response.status === 401,
      };
    case MTM_REPORTS_SUCCESS:
      return {
        ...state,
        mtmReport: action.payload,
        isMTMReportLoading: false,
      };
    case MTM_REPORTS_FAILURE:
      return {
        ...state,
        mtmReportError: {
          error: true,
          details: action.payload.response,
        },
        isMTMReportLoading: false,
        authError: action.payload.response.status === 401,
      };
    case VAR_REPORTS_SUCCESS:
      return {
        ...state,
        varReport: action.payload,
        isVARReportLoading: false,
      };
    case VAR_REPORTS_FAILURE:
      return {
        ...state,
        varReportError: {
          error: true,
          details: action.payload.response,
        },
        isVARReportLoading: false,
        authError: action.payload.response.status === 401,
      };
    case PROJECTED_VAR_REPORTS_SUCCESS:
      return {
        ...state,
        projectedVarReport: action.payload,
        isProjectedVARReportLoading: false,
      };
    case PROJECTED_VAR_REPORTS_FAILURE:
      return {
        ...state,
        projectedVarReportError: {
          error: true,
          details: action.payload.response,
        },
        isProjectedVARReportLoading: false,
        authError: action.payload.response.status === 401,
      };
    case SLICES_FAILURE:
      return {
        ...state,
        slicesError: {
          error: true,
          details: action.payload.response,
        },
        isSlicesLoading: false,
        authError: action.payload.response.status === 401,
      };
    case SLICES_SUCCESS:
      return {
        ...state,
        slices: action.payload,
        isSlicesLoading: false,
      };
    case ALERTS_FAILURE:
      return {
        ...state,
        alertsError: {
          error: true,
          details: action.payload.response,
        },
        authError: action.payload.response.status === 401,
      };
    case ALERTS_SUCCESS:
      return {
        ...state,
        alerts: action.payload.value,
      };
    case SUMMARY_FAILURE:
      return {
        ...state,
        summaryError: {
          error: true,
          details: action.payload.response,
        },
        isSummaryLoading: false,
        authError: action.payload.response.status === 401,
      };
    case SUMMARY_SUCCESS:
      return {
        ...state,
        summary: action.payload,
        isSummaryLoading: false,
        error: false,
      };
    case INSTRUMENT_FAILURE:
      return {
        ...state,
        instrumentError: {
          error: true,
          details: action.payload.response,
        },
        isInstrumentLoading: false,
        authError: action.payload.response.status === 401,
      };
    case INSTRUMENT_SUCCESS:
      return {
        ...state,
        instrument: action.payload,
        isInstrumentLoading: false,
      };
    case INSTRUMENT_SUMMARY_SUCCESS:
      return {
        ...state,
        instrumentSummary: action.payload,
        isInstrumentSummaryLoading: false,
      };
    case INSTRUMENT_SUMMARY_FAILURE:
      return {
        ...state,
        instrumentSummaryError: {
          error: true,
          details: action.payload.response,
        },
        isInstrumentSummaryLoading: false,
        authError: action.payload.response.status === 401,
      };
    case CORRESPONDENT_SUCCESS:
      return {
        ...state,
        correspondent: action.payload,
        isCorrespondentLoading: false,
      };
    case CORRESPONDENT_FAILURE:
      return {
        ...state,
        correspondentError: {
          error: true,
          details: action.payload.response,
        },
        isCorrespondentLoading: false,
        authError: action.payload.response.status === 401,
      };
    case SET_SLICE:
      return {
        ...state,
        slice: action.payload,
      };
    case SET_TKR_CUSIP:
      return {
        ...state,
        tkrCusip: action.payload,
      };
    case SET_SUMMARY_DATE:
      return {
        ...state,
        summaryDate: action.payload,
      };
    case FAILURE:
      return {
        ...state,
        errorDetails: action.payload.response,
        error: true,
        isSummaryLoading: false,
      };
    default:
      return state;
  }
};

export { reducer as reportReducer };
